import React from 'react';
import Badge from 'react-bootstrap/Badge';
import { strings } from '../../resources/strings';
import { useAuth } from '../../contexts/AuthContext';
import { OrderGeneralOption } from '../../enums/OrderGeneralOption';
import { ProfileType } from '../../enums/ProfileType';
import { SeparationType } from '../../enums/SeparationType';
import { StateOrderIdentification } from '../../enums/StateOrderIdentification';
import { StatePrescriptionIdentification } from '../../enums/StatePrescriptionIdentification';
import { useOrderUtils } from '../../hooks/useOrderUtils';
import { SessionHelper } from '../../helpers/SessionHelper';
import { OrderForListing } from '../../models/OrderForListing';
import { StateLog } from '../../models/StateLog';
import { StatePrescriptionLog } from '../../models/StatePrescriptionLog';
import { DropdownButton, DropdownButtonColor, DropdownButtonDirection } from '../../common-components/DropdownButton/DropdownButton';
import { GlobalPickerOrderManagementButton } from '../GlobalPickerOrderManagementButton/GlobalPickerOrderManagementButton';
import { AssemblerOrderManagementButton } from '../AssemblerOrderManagementButton/AssemblerOrderManagementButton';

interface IOrderOptionsCellProps {
  separationType: SeparationType;
  orderForListing: OrderForListing;
  currentState?: StateLog;
  currentStatePrescription?: StatePrescriptionLog;
  userProfile?: ProfileType;
  handleGeneralOption: (orderForListing: OrderForListing, key: any) => void;
  changeState: (orderForListing: OrderForListing, id: any) => void;
  handlePrescriptionOption: (orderForListing: OrderForListing, key: any) => void;
  statePrescriptionChanged?: (newStateId?: number) => void;
  stateOrderChanged?: (newStateId?: number) => void;
  refresh?: (orderId?: number) => void;
}

export const OrderOptionsCell: React.FC<IOrderOptionsCellProps> = ({
  separationType,
  orderForListing,
  currentState,
  currentStatePrescription,
  userProfile,
  handleGeneralOption,
  changeState,
  handlePrescriptionOption,
  statePrescriptionChanged,
  stateOrderChanged,
  refresh
}) => {

  const { session } = useAuth();
  const userCanSeeSimpleTable = SessionHelper.canSeeSimpleTable(session);
  const userCanManageTransport = SessionHelper.canManageTransport(session);
  const userCanSeeOrderDetails = SessionHelper.canSeeOrderDetails(session);
  const userCanSeeTransport = SessionHelper.canSeeTransport(session);
  const userCanManageLensCuttingMachineIntegration = SessionHelper.canManageLensCuttingMachineIntegration(session);

  const {
    getButtonPrescriptionOptions,
    getPreviousDropdownItems,
    getNextDropdownItems,
    getDeliveryNextDropdownItems,
    orderIsCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOriginAndCanBeSelected
  } = useOrderUtils();

  const genericalOptionsItems = [
    {
      key: OrderGeneralOption.Historic,
      text: strings.Historic
    },
    {
      key: OrderGeneralOption.OrderNotes,
      text: strings.OrderNotes
    }
  ];

  if (!userCanSeeSimpleTable && userCanManageTransport || userCanSeeTransport) {
    genericalOptionsItems.push({
      key: OrderGeneralOption.ManageTransport,
      text: strings.ManageTransport
    });
  }

  let optionsByPrescriptionRender = null;

  if (separationType === SeparationType.Prescription) {
    if (orderForListing.order.shopifyFormatedClosedAt) {
      genericalOptionsItems.push({
        key: orderForListing.order.isPhysicalStore ? OrderGeneralOption.Recipes : OrderGeneralOption.RecipesV2,
        text: strings.Recipes
      });
    }

    genericalOptionsItems.push({
      key: OrderGeneralOption.PrintTSO,
      text: `${strings.TSO} A4 (v1)`
    });

    genericalOptionsItems.push({
      key: OrderGeneralOption.PrintTSOV2A5,
      text: `${strings.TSO} A5 (v2)`
    });

    if (SessionHelper.canPrintTSOWithCssBarcode(session)) {
      genericalOptionsItems.push({
        key: OrderGeneralOption.PrintTSOCssBarcode,
        text: `${strings.TSO} A4 (v1) - teste`
      });

      genericalOptionsItems.push({
        key: OrderGeneralOption.PrintTSOV2A5CssBarcode,
        text: `${strings.TSO} A5 (v2) - teste`
      });
    }

    const currentStatePrescriptionId = currentStatePrescription?.currentStateId || StatePrescriptionIdentification.SemReceita;

    optionsByPrescriptionRender = (
      <>
        <DropdownButton
          items={getButtonPrescriptionOptions(currentStatePrescriptionId, orderForListing.order.isPhysicalStore,
            orderForListing.order.customer?.shopifyId, orderForListing.order.isCrossChannelSale,
            orderForListing.order.locationId, orderForListing.order.originLocationId)}
          defaultStyle={true}
          small={true}
          buttonColor={DropdownButtonColor.Info}
          text={''}
          clickItem={(key) => handlePrescriptionOption(orderForListing, key)}
          marginLeft={'3px'}
        />
      </>
    );
  }

  if (!userCanSeeSimpleTable) {
    const currentStateNeedsNFe = currentState?.currentStateId === StateOrderIdentification.NFImpressa ||
      currentState?.currentStateId === StateOrderIdentification.AguardandoPacking ||
      currentState?.currentStateId === StateOrderIdentification.ProntoParaEnvio ||
      currentState?.currentStateId === StateOrderIdentification.Enviado ||
      currentState?.currentStateId === StateOrderIdentification.PedidoFaturado;
    if (currentStateNeedsNFe || session?.user?.profileType == ProfileType.Admin) {
      genericalOptionsItems.push({
        key: OrderGeneralOption.ListNFes,
        text: strings.ListNFes
      });
    }
  }

  if (userCanSeeOrderDetails) {
    genericalOptionsItems.push({
      key: OrderGeneralOption.OrderDetails,
      text: strings.OrderDetails
    });
  }

  if (separationType === SeparationType.Prescription) {
    const currentStatePrescriptionId = currentStatePrescription?.currentStateId || StatePrescriptionIdentification.SemReceita;
    if (currentStatePrescriptionId > StatePrescriptionIdentification.SemReceita) {
      genericalOptionsItems.push({
        key: OrderGeneralOption.DigitalLensCertificates,
        text: strings.DigitalLensCertificates
      });
    }
    const orderLocationId = orderForListing?.order?.locationId || 0;
    const orderLocation = orderLocationId > 0 && session && session.locations && session.locations.length > 0
      ? session.locations.find(l => l.id === orderLocationId)
      : null;

    const waitingForAssemblyState =
      currentStatePrescriptionId === StatePrescriptionIdentification.AguardandoMontagemInterna ||
      currentStatePrescriptionId === StatePrescriptionIdentification.AguardandoMontagemExterna;
    if (
      (waitingForAssemblyState && orderForListing?.order?.isPhysicalStore && userCanManageLensCuttingMachineIntegration && orderLocation?.cutLensesByMachine === true)
      ||
      (waitingForAssemblyState && orderLocation?.cutLensesByMachine === true && session?.user?.profileType === ProfileType.Admin)
      ||
      (currentStatePrescriptionId === StatePrescriptionIdentification.AguardandoMontagemInterna && !orderForListing?.order?.isPhysicalStore && userCanManageLensCuttingMachineIntegration && orderLocation?.cutLensesByMachine === true)
    ) {
      genericalOptionsItems.push({
        key: OrderGeneralOption.ManageLensCuttingMachineIntegration,
        text: strings.IntegrationWithLensCuttingMachine
      });
    }
  }

  const genericalOptionsRender = (
    <DropdownButton
      items={genericalOptionsItems}
      defaultStyle={true}
      small={true}
      buttonColor={DropdownButtonColor.Secondary}
      text={''}
      clickItem={(key) => handleGeneralOption(orderForListing, key)}
      marginRight={'3px'}
    />
  );

  if (orderForListing.order.shopifyFormatedClosedAt || orderForListing.order.shopifyFormatedCancelledAt) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ marginRight: '5px' }}>
          <Badge style={{ minWidth: '74px' }} bg={orderForListing.order.shopifyFormatedCancelledAt ? 'danger' : 'info'}>
            {orderForListing.order.shopifyFormatedCancelledAt ? 'Cancelado' : 'Fechado'}
          </Badge>
        </div>
        {genericalOptionsRender}
        {!userCanSeeSimpleTable && session?.user?.profileType !== ProfileType.Viewer ? (
          <DropdownButton
            items={getDeliveryNextDropdownItems(
              currentState?.currentStateId || orderForListing.stateLog?.currentStateId,
              session?.user?.profileType == ProfileType.Admin || session?.user?.profileType == ProfileType.Ecommerce
            )}
            defaultStyle={true}
            small={true}
            direction={DropdownButtonDirection.DropToRight}
            buttonColor={DropdownButtonColor.Success}
            text={' '}
            clickItem={(key) => changeState(orderForListing, key)}
            marginLeft={'3px'}
          />
        ) : null}
      </div>
    );
  }

  if (session?.user?.profileType === ProfileType.GlobalPicker) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {genericalOptionsRender}
        <GlobalPickerOrderManagementButton
          separationType={separationType}
          currentState={currentState}
          currentStatePrescription={currentStatePrescription}
          orderId={orderForListing.order.id}
          orderNumber={orderForListing.order.number}
          orderDataSource={orderForListing.order.dataSource}
          orderIsPhysicalStore={orderForListing.order.isPhysicalStore}
          statePrescriptionChanged={statePrescriptionChanged}
          stateOrderChanged={stateOrderChanged}
          modalRecipesTitle={`${strings.Recipes} ${orderForListing.order.number}`}
          modalClosed={() => refresh && refresh(orderForListing.order.id)}
          customerId={orderForListing.order.customerId}
        />
      </div>
    );
  }

  if (session?.user?.profileType === ProfileType.Assembler) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <AssemblerOrderManagementButton
          separationType={separationType}
          orderId={orderForListing.order.id}
          orderNumber={orderForListing.order.number}
          modalClosed={() => refresh && refresh(orderForListing.order.id)}
        />
      </div>
    );
  }

  if (session?.user?.profileType === ProfileType.Viewer) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {genericalOptionsRender}
        {optionsByPrescriptionRender}
      </div>
    );
  }

  if (orderForListing.order?.isCrossChannelSale &&
    !orderIsCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOriginAndCanBeSelected(orderForListing.order)
  ) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {genericalOptionsRender}
        {optionsByPrescriptionRender}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }} data-current-state-id={currentState?.currentStateId || orderForListing.stateLog?.currentStateId}>
      {genericalOptionsRender}
      <DropdownButton
        items={
          getPreviousDropdownItems(currentState?.currentStateId || orderForListing.stateLog?.currentStateId, userProfile)
            .filter(item => orderForListing.order.isPhysicalStore ? true : +(item?.key || 0) !== StateOrderIdentification.PendenteVendedor)
        }
        defaultStyle={true}
        small={true}
        direction={DropdownButtonDirection.DropToLeft}
        buttonColor={DropdownButtonColor.Success}
        text={' '}
        clickItem={(key) => changeState(orderForListing, key)}
      />
      <DropdownButton
        items={getNextDropdownItems(currentState?.currentStateId || orderForListing.stateLog?.currentStateId, userProfile)}
        defaultStyle={true}
        small={true}
        direction={DropdownButtonDirection.DropToRight}
        buttonColor={DropdownButtonColor.Success}
        text={' '}
        clickItem={(key) => changeState(orderForListing, key)}
        marginLeft={'3px'}
      />
      {optionsByPrescriptionRender}
    </div>
  );

}
